import { api } from './api'

export interface CheckoutSessionRequest {
  plan_id: string
  billing_cycle: 'monthly' | 'yearly'
  /** charge in this currency when the plan offers it (e.g. XAF in Africa) */
  currency?: 'usd' | 'eur' | 'xaf'
  /** school plans: number of pupils (defaults to the school's pupil count) */
  seats?: number
  success_url?: string
  cancel_url?: string
}

export interface CheckoutSessionResponse {
  checkout_session_id: string
  checkout_url: string
  message: string
}

export interface Subscription {
  plan_name: string
  plan_tier: string
  status: string
  billing_cycle: string
  starts_at: string
  ends_at: string | null
  trial_ends_at: string | null
  is_trial: boolean
  is_active: boolean
  can_cancel: boolean
  can_manage_billing?: boolean
}

export interface SubscriptionStatus {
  has_subscription: boolean
  subscription?: Subscription
}

class PaymentService {
  private basePath = '/payment'

  async createCheckoutSession(
    data: CheckoutSessionRequest
  ): Promise<CheckoutSessionResponse> {
    const response = await api.post<CheckoutSessionResponse>(`${this.basePath}/checkout`, data)
    return response.data
  }

  async handleCheckoutSuccess(sessionId: string): Promise<{
    message: string
    subscription: Subscription
  }> {
    const response = await api.post<{message: string; subscription: Subscription}>(
      `${this.basePath}/checkout/success?session_id=${sessionId}`
    )
    return response.data
  }

  async cancelSubscription(immediately: boolean = false): Promise<{
    message: string
    subscription: Subscription
  }> {
    const response = await api.post<{message: string; subscription: Subscription}>(
      `${this.basePath}/cancel?immediately=${immediately}`
    )
    return response.data
  }

  /** Whether card payment is switched on (otherwise plans are arranged with the team). */
  async getConfig(): Promise<{ online_payment: boolean }> {
    try {
      const response = await api.get<{ online_payment: boolean }>(`${this.basePath}/config`)
      return response.data
    } catch {
      return { online_payment: false }
    }
  }

  /** Stripe's billing page: card, invoices, cancellation. */
  async openBillingPortal(): Promise<void> {
    const response = await api.post<{ url: string }>(`${this.basePath}/portal`)
    window.location.href = response.data.url
  }

  async getSubscriptionStatus(): Promise<SubscriptionStatus> {
    const response = await api.get<SubscriptionStatus>(`${this.basePath}/subscription`)
    return response.data
  }

  /**
   * Redirect to Stripe checkout page
   */
  async redirectToCheckout(
    planId: string,
    billingCycle: 'monthly' | 'yearly'
  ): Promise<void> {
    const sessionData = await this.createCheckoutSession({
      plan_id: planId,
      billing_cycle: billingCycle,
      success_url: `${window.location.origin}/app/upgrade/success?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/app/pricing`,
    })

    // Redirect to Stripe checkout
    window.location.href = sessionData.checkout_url
  }
}

export const paymentService = new PaymentService()
export default paymentService
